'use client';

import { useTranslation } from 'react-i18next';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { shouldShowSpotsLeft } from '~/(tenant)/_lib/class-scarcity';

interface ClassStatusBadgeProps {
  spotsRemaining: number;
  isFull: boolean;
  waitlistEnabled: boolean;
  /** class_types.show_spots_remaining — omitted means show (the DB default). */
  showSpotsRemaining?: boolean;
  /** class_types.show_spots_remaining_last_only — omitted means always show (the DB default). */
  lastSpotOnly?: boolean;
}

/**
 * Corner badge on a native class card. Renders at most one state:
 *   full + waitlist   → "Waitlist"
 *   full, no waitlist → "Full"
 *   seats left        → "N spots left" (unless the template opts out)
 *   otherwise         → nothing
 *
 * Shared by every native class rail so the states can't drift apart.
 */
export function ClassStatusBadge({
  spotsRemaining,
  isFull,
  waitlistEnabled,
  showSpotsRemaining,
  lastSpotOnly,
}: ClassStatusBadgeProps) {
  const t = useNativeTokens();
  const { t: translate } = useTranslation();

  const label = isFull
    ? waitlistEnabled
      ? translate('common:nativeHome.waitlist')
      : translate('common:nativeHome.classFull')
    : shouldShowSpotsLeft({ spotsRemaining, showSpotsRemaining, lastSpotOnly })
      ? translate('common:nativeHome.spotsRemaining', { count: spotsRemaining })
      : null;

  if (!label) return null;

  const isDark = isFull;

  return (
    <div
      style={{
        position: 'absolute',
        top: 12,
        left: 12,
        padding: '5px 9px',
        background: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.92)',
        borderRadius: 999,
        fontFamily: t.fontMono,
        fontSize: 8,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: isDark ? '#fff' : '#1F1813',
      }}
    >
      {label}
    </div>
  );
}
